import React, { useState, useEffect, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetFirst25AllQuery } from '@/store/api';
import { UserRole } from '@sportspredict/shared';
import type { User } from '@/types/layout.types';

interface UsePopupCategoriesReturn {
	popupCategories: string[];
	setPopupCategories: React.Dispatch<React.SetStateAction<string[]>>;
}

export function usePopupCategories(user: User | null | undefined): UsePopupCategoriesReturn {
	const [popupCategories, setPopupCategories] = useState<string[]>([]);
	const dismissedRef = useRef<Set<string>>(new Set());

	const { data: categories } = useGetFirst25AllQuery(
		user && user.role === UserRole.User ? { userId: user.id } : skipToken
	);

	useEffect(() => {
		if (!categories?.length) return;

		const pending = (categories as string[]).filter((c) => !dismissedRef.current.has(c));
		if (!pending.length) return;

		setPopupCategories((current) => {
			const isSame = current.length === pending.length && current.every((c, i) => c === pending[i]);
			return isSame ? current : pending;
		});
	}, [categories]);

	const dismissCategories: React.Dispatch<React.SetStateAction<string[]>> = (updater) => {
		setPopupCategories((prev) => {
			const next = typeof updater === 'function' ? updater(prev) : updater;
			if (next.length === 0 && prev.length > 0) {
				prev.forEach((c) => dismissedRef.current.add(c));
			}
			return next;
		});
	};

	return { popupCategories, setPopupCategories: dismissCategories };
}
